import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    WorkspaceSwitcher
} from "./brand";
import {
    type NCoreUIKitIcon,
    NCoreUIKitTheme,
    Breadcrumb,
    Separator,
    Sidebar,
    Avatar,
    Button,
    Dialog,
    Switch,
    Alert,
    Badge,
    Empty,
    Table,
    Card,
    Item,
    Tabs,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    EllipsisVertical,
    LayoutDashboard,
    MessageSquare,
    ChartColumn,
    CircleHelp,
    Smartphone,
    KeyRound,
    Settings,
    Trash2,
    Folder,
    Laptop,
    Users,
    Plus,
    Bell,
    Eye
} from "lucide-react-native";
import {
    createActionIcon,
    createKitIcon
} from "../components/kitIcons";

type MethodKey = "app" | "sms";

type DeviceKey = "macbook" | "iphone" | "windows" | "pixel";

type MatrixKey = "comments" | "mentions" | "assignments" | "digest";

type ChannelKey = "email" | "mobile";

type Method = {
    icon: NCoreUIKitIcon;
    isDefault: boolean;
    key: MethodKey;
};

type Session = {
    icon: NCoreUIKitIcon;
    isCurrent: boolean;
    key: DeviceKey;
};

const LayoutDashboardIcon = createKitIcon(LayoutDashboard);

const EllipsisVerticalIcon = createKitIcon(EllipsisVertical);

const MessageSquareIcon = createKitIcon(MessageSquare);

const ChartColumnIcon = createKitIcon(ChartColumn);

const SmartphoneIcon = createKitIcon(Smartphone);

const CircleHelpIcon = createKitIcon(CircleHelp);

const KeyRoundIcon = createKitIcon(KeyRound);

const SettingsIcon = createKitIcon(Settings);

const Trash2Icon = createActionIcon(Trash2);

const FolderIcon = createKitIcon(Folder);

const LaptopIcon = createKitIcon(Laptop);

const UsersIcon = createKitIcon(Users);

const PlusIcon = createKitIcon(Plus);

const BellIcon = createKitIcon(Bell);

const EyeIcon = createKitIcon(Eye);

const SECTIONS = [
    "profile",
    "account",
    "security",
    "notifications",
    "integrations",
    "billing"
] as const;

const METHODS: Array<Method> = [
    {
        icon: SmartphoneIcon,
        isDefault: true,
        key: "app"
    },
    {
        icon: MessageSquareIcon,
        isDefault: false,
        key: "sms"
    }
];

const PASSKEYS = [
    "macbook",
    "iphone"
] as const;

const SESSIONS: Array<Session> = [
    {
        icon: LaptopIcon,
        isCurrent: true,
        key: "macbook"
    },
    {
        icon: SmartphoneIcon,
        isCurrent: false,
        key: "iphone"
    },
    {
        icon: LaptopIcon,
        isCurrent: false,
        key: "windows"
    },
    {
        icon: SmartphoneIcon,
        isCurrent: false,
        key: "pixel"
    }
];

const MATRIX: Array<MatrixKey> = [
    "comments",
    "mentions",
    "assignments",
    "digest"
];

const CHANNELS: Array<ChannelKey> = [
    "email",
    "mobile"
];

const INITIAL_MATRIX: Record<string, boolean> = {
    "assignments:mobile": false,
    "assignments:email": true,
    "comments:mobile": true,
    "mentions:mobile": true,
    "comments:email": true,
    "digest:mobile": false,
    "mentions:email": true,
    "digest:email": true
};

const stylesheet = StyleSheet.create({
    bar: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    tabs: {
        alignItems: "flex-start"
    },
    column: {
        flexDirection: "column"
    },
    row: {
        alignItems: "center",
        flexDirection: "row"
    },
    titleRow: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    card: {
        width: "100%"
    },
    list: {
        width: "auto"
    },
    content: {
        minWidth: 0,
        flex: 1
    },
    grow: {
        minWidth: 0,
        flex: 1
    },
    page: {
        minWidth: 0
    }
});

const handlePress = () => undefined;

const Settings01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        controlHeights,
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        section,
        setSection
    ] = useState<string>("security");

    const [
        isTwoFactorOn,
        setIsTwoFactorOn
    ] = useState(true);

    const [
        passkeys,
        setPasskeys
    ] = useState<Array<string>>([
        ...PASSKEYS
    ]);

    const [
        passkeyToRemove,
        setPasskeyToRemove
    ] = useState<string | null>(null);

    const [
        sessions,
        setSessions
    ] = useState<Array<Session>>(SESSIONS);

    const [
        matrix,
        setMatrix
    ] = useState<Record<string, boolean>>(INITIAL_MATRIX);

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const isRoomy = width >= shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth;

    const handleMatrixToggle = (key: string) => () => setMatrix({
        ...matrix,
        [key]: !matrix[key]
    });

    const handlePasskeyRemove = (key: string) => () => setPasskeyToRemove(key);

    const handlePasskeyRemoveClosed = () => setPasskeyToRemove(null);

    const handleSessionClose = (key: DeviceKey) => () => setSessions(sessions.filter((session) => session.key !== key));

    const handleOthersClose = () => setSessions(sessions.filter((session) => session.isCurrent));

    const renderNavItem = (title: string, icon: NCoreUIKitIcon, isActive: boolean = false) => <Sidebar.MenuItem>
        <Sidebar.MenuButton
            onPress={handlePress}
            isActive={isActive}
            title={title}
            icon={icon}
        />
    </Sidebar.MenuItem>;

    const renderTwoFactor = () => <Card
        style={stylesheet.card}
        variant="outline"
    >
        <Card.Header>
            <View
                style={[
                    stylesheet.titleRow,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Card.Title>
                    {t("blocks.settings01.twoFactor.title")}
                </Card.Title>
                {
                    isTwoFactorOn ?
                        <Badge
                            title={t("blocks.settings01.twoFactor.on")}
                            variant="tonal"
                            type="success"
                            size="small"
                        />
                    :
                        null
                }
            </View>
            <Card.Description>
                {t("blocks.settings01.twoFactor.description")}
            </Card.Description>
            <Card.Action>
                <Switch
                    accessibilityLabel={t("blocks.settings01.twoFactor.title")}
                    onCheckedChange={setIsTwoFactorOn}
                    isChecked={isTwoFactorOn}
                />
            </Card.Action>
        </Card.Header>
        <Card.Content>
            <Item.Group
                isDivided={true}
            >
                {
                    METHODS.map((method) => <Item
                        isDisabled={!isTwoFactorOn}
                        key={method.key}
                    >
                        <Item.Media
                            icon={method.icon}
                            variant="icon"
                        />
                        <Item.Content>
                            <View
                                style={[
                                    stylesheet.titleRow,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Item.Title>
                                    {t(`blocks.settings01.method.${method.key}.name`)}
                                </Item.Title>
                                {
                                    method.isDefault ?
                                        <Badge
                                            title={t("blocks.settings01.method.default")}
                                            variant="tonal"
                                            type="neutral"
                                            size="small"
                                        />
                                    :
                                        null
                                }
                            </View>
                            <Item.Description>
                                {t(`blocks.settings01.method.${method.key}.note`)}
                            </Item.Description>
                        </Item.Content>
                        <Item.Actions>
                            <Button
                                title={t(`blocks.settings01.method.${method.key}.action`)}
                                isDisabled={!isTwoFactorOn}
                                onPress={handlePress}
                                variant="ghost"
                                type="neutral"
                                size="small"
                            />
                        </Item.Actions>
                    </Item>)
                }
            </Item.Group>
        </Card.Content>
        <Card.Footer>
            <View
                style={[
                    stylesheet.bar,
                    stylesheet.grow,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="bodySmallSize"
                    style={stylesheet.grow}
                    color="mid"
                >
                    {t("blocks.settings01.twoFactor.backupHint")}
                </Text>
                <Button
                    title={t("blocks.settings01.twoFactor.backup")}
                    isDisabled={!isTwoFactorOn}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="outline"
                    type="neutral"
                    icon={EyeIcon}
                />
            </View>
        </Card.Footer>
    </Card>;

    const renderPasskeys = () => <Card
        style={stylesheet.card}
        variant="outline"
    >
        <Card.Header>
            <Card.Title>
                {t("blocks.settings01.passkeys.title")}
            </Card.Title>
            <Card.Description>
                {t("blocks.settings01.passkeys.description")}
            </Card.Description>
            <Card.Action>
                <Button
                    title={t("blocks.settings01.passkeys.add")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    variant="outline"
                    icon={PlusIcon}
                    type="neutral"
                />
            </Card.Action>
        </Card.Header>
        <Card.Content>
            <Item.Group
                isDivided={true}
            >
                {
                    passkeys.map((key) => <Item
                        key={key}
                    >
                        <Item.Media
                            icon={KeyRoundIcon}
                            variant="icon"
                        />
                        <Item.Content>
                            <Item.Title>
                                {t(`blocks.settings01.passkeys.${key}.name`)}
                            </Item.Title>
                            <Item.Description>
                                {t(`blocks.settings01.passkeys.${key}.meta`)}
                            </Item.Description>
                        </Item.Content>
                        <Item.Actions>
                            <Button
                                accessibilityLabel={t(`blocks.settings01.passkeys.${key}.remove`)}
                                onPress={handlePasskeyRemove(key)}
                                icon={Trash2Icon}
                                variant="ghost"
                                type="neutral"
                                size="small"
                            />
                        </Item.Actions>
                    </Item>)
                }
            </Item.Group>
            <Dialog
                primaryButtonProps={{
                    onPress: ({
                        closeAnimation
                    }) => {
                        const key = passkeyToRemove;

                        closeAnimation(() => setPasskeys((current) => current.filter((passkey) => passkey !== key)));
                    },
                    title: t("blocks.settings01.passkeys.removeConfirm")
                }}
                content={t("blocks.settings01.passkeys.removeText", [
                    passkeyToRemove ? t(`blocks.settings01.passkeys.${passkeyToRemove}.name`) : ""
                ])}
                title={t("blocks.settings01.passkeys.removeTitle")}
                onClosed={handlePasskeyRemoveClosed}
                isOpen={passkeyToRemove !== null}
                secondaryButtonProps={{
                    onPress: ({
                        closeAnimation
                    }) => closeAnimation()
                }}
                onOverlayPress={({
                    closeAnimation
                }) => closeAnimation()}
                variant="yes-no"
                type="danger"
            />
        </Card.Content>
    </Card>;

    const renderSession = (session: Session) => <Table.Row
        key={session.key}
    >
        <Table.Cell>
            <View
                style={[
                    stylesheet.row,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Avatar
                    icon={session.icon}
                    size="xSmall"
                />
                <View
                    style={stylesheet.column}
                >
                    <Text
                        variant="labelMediumSize"
                        color="high"
                    >
                        {t(`blocks.settings01.sessions.${session.key}.device`)}
                    </Text>
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {t(`blocks.settings01.sessions.${session.key}.browser`)}
                    </Text>
                </View>
            </View>
        </Table.Cell>
        <Table.Cell>
            <Text
                variant="bodyMediumSize"
                color="mid"
            >
                {t(`blocks.settings01.sessions.${session.key}.place`)}
            </Text>
        </Table.Cell>
        <Table.Cell>
            <View
                style={[
                    stylesheet.titleRow,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {t(`blocks.settings01.sessions.${session.key}.last`)}
                </Text>
                {
                    session.isCurrent ?
                        <Badge
                            title={t("blocks.settings01.sessions.current")}
                            variant="tonal"
                            type="success"
                            size="small"
                        />
                    :
                        null
                }
            </View>
        </Table.Cell>
        <Table.Cell
            isShrink={true}
            align="end"
        >
            {
                session.isCurrent ?
                    null
                :
                    <Button
                        accessibilityLabel={t(`blocks.settings01.sessions.${session.key}.closeLabel`)}
                        title={t("blocks.settings01.sessions.close")}
                        onPress={handleSessionClose(session.key)}
                        spreadBehaviour="free"
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
            }
        </Table.Cell>
    </Table.Row>;

    const renderSessions = () => <Card
        style={stylesheet.card}
        variant="outline"
    >
        <Card.Header>
            <Card.Title>
                {t("blocks.settings01.sessions.title")}
            </Card.Title>
            <Card.Description>
                {t("blocks.settings01.sessions.description")}
            </Card.Description>
            <Card.Action>
                <Button
                    title={t("blocks.settings01.sessions.closeOthers")}
                    isDisabled={sessions.length < 2}
                    onPress={handleOthersClose}
                    spreadBehaviour="free"
                    variant="tonal"
                    type="danger"
                />
            </Card.Action>
        </Card.Header>
        <Card.Content>
            <Table
                caption={t("blocks.settings01.sessions.title")}
                isCaptionHidden={true}
                size="large"
            >
                <Table.Header>
                    <Table.Row>
                        <Table.Head>
                            {t("blocks.settings01.sessions.device")}
                        </Table.Head>
                        <Table.Head>
                            {t("blocks.settings01.sessions.place")}
                        </Table.Head>
                        <Table.Head>
                            {t("blocks.settings01.sessions.last")}
                        </Table.Head>
                        <Table.Head
                            isShrink={true}
                        >
                            <View
                                style={{
                                    width: controlHeights.small
                                }}
                            />
                        </Table.Head>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {sessions.map(renderSession)}
                </Table.Body>
            </Table>
        </Card.Content>
    </Card>;

    const renderMatrix = () => <Card
        style={stylesheet.card}
        variant="outline"
    >
        <Card.Header>
            <Card.Title>
                {t("blocks.settings01.matrix.title")}
            </Card.Title>
            <Card.Description>
                {t("blocks.settings01.matrix.description")}
            </Card.Description>
        </Card.Header>
        <Card.Content>
            <Table
                caption={t("blocks.settings01.matrix.title")}
                isCaptionHidden={true}
                size="large"
            >
                <Table.Header>
                    <Table.Row>
                        <Table.Head>
                            {t("blocks.settings01.matrix.event")}
                        </Table.Head>
                        {
                            CHANNELS.map((channel) => <Table.Head
                                isShrink={true}
                                key={channel}
                            >
                                {t(`blocks.settings01.matrix.${channel}`)}
                            </Table.Head>)
                        }
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {
                        MATRIX.map((key) => <Table.Row
                            key={key}
                        >
                            <Table.Cell>
                                <View
                                    style={stylesheet.column}
                                >
                                    <Text
                                        variant="labelMediumSize"
                                        color="high"
                                    >
                                        {t(`blocks.settings01.matrix.${key}.name`)}
                                    </Text>
                                    <Text
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {t(`blocks.settings01.matrix.${key}.note`)}
                                    </Text>
                                </View>
                            </Table.Cell>
                            {
                                CHANNELS.map((channel) => <Table.Cell
                                    isShrink={true}
                                    key={channel}
                                >
                                    <Switch
                                        accessibilityLabel={t(`blocks.settings01.matrix.${key}.${channel}Label`)}
                                        onPress={handleMatrixToggle(`${key}:${channel}`)}
                                        isChecked={matrix[`${key}:${channel}`]}
                                    />
                                </Table.Cell>)
                            }
                        </Table.Row>)
                    }
                </Table.Body>
            </Table>
        </Card.Content>
    </Card>;

    const renderSecurity = () => <View
        style={[
            stylesheet.column,
            {
                gap: spaces.spacingLg
            }
        ]}
    >
        {renderTwoFactor()}
        {renderPasskeys()}
        {renderSessions()}
        {renderMatrix()}
        <Alert
            description={t("blocks.settings01.danger.description")}
            actions={[
                {
                    title: t("blocks.settings01.danger.delete"),
                    onPress: handlePress
                }
            ]}
            title={t("blocks.settings01.danger.title")}
            type="danger"
        />
    </View>;

    const renderOther = () => <Empty
        variant="outline"
    >
        <Empty.Header>
            <Empty.Media
                icon={SettingsIcon}
            />
            <Empty.Title>
                {t(`blocks.settings01.sections.${section}`)}
            </Empty.Title>
            <Empty.Description>
                {t("blocks.settings01.otherDescription")}
            </Empty.Description>
        </Empty.Header>
    </Empty>;

    return <Sidebar.Provider
        variant="inset"
    >
        <Sidebar
            collapseBehaviour={isRoomy ? "offcanvas" : "icon"}
            title={t("blocks.settings01.navTitle")}
        >
            <Sidebar.Header>
                <WorkspaceSwitcher
                    onPress={handlePress}
                />
            </Sidebar.Header>
            <Sidebar.Content>
                <Sidebar.Group>
                    <Sidebar.Menu>
                        {renderNavItem(t("blocks.settings01.navOverview"), LayoutDashboardIcon)}
                        <Sidebar.MenuItem>
                            <Sidebar.MenuButton
                                title={t("blocks.settings01.navProjects")}
                                onPress={handlePress}
                                icon={FolderIcon}
                            />
                            <Sidebar.MenuBadge
                                count={12}
                            />
                        </Sidebar.MenuItem>
                        {renderNavItem(t("blocks.settings01.navTeam"), UsersIcon)}
                        {renderNavItem(t("blocks.settings01.navReports"), ChartColumnIcon)}
                    </Sidebar.Menu>
                </Sidebar.Group>
                <Sidebar.Group
                    title={t("blocks.settings01.navAccount")}
                >
                    <Sidebar.Menu>
                        {renderNavItem(t("blocks.settings01.navSettings"), SettingsIcon, true)}
                        {renderNavItem(t("blocks.settings01.navHelp"), CircleHelpIcon)}
                    </Sidebar.Menu>
                </Sidebar.Group>
            </Sidebar.Content>
            <Sidebar.Footer>
                <Sidebar.Menu>
                    <Sidebar.MenuItem>
                        <Sidebar.MenuButton
                            renderIcon={() => <Avatar
                                title={t("blocks.settings01.userName")}
                                size="xSmall"
                            />}
                            subTitle={t("blocks.settings01.userEmail")}
                            title={t("blocks.settings01.userName")}
                        />
                        <Sidebar.MenuAction
                            title={t("blocks.settings01.userMenu")}
                            icon={EllipsisVerticalIcon}
                            onPress={handlePress}
                            isShowOnHover={false}
                        />
                    </Sidebar.MenuItem>
                </Sidebar.Menu>
            </Sidebar.Footer>
        </Sidebar>
        <Sidebar.Inset>
            <View
                style={[
                    stylesheet.bar,
                    {
                        paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        paddingVertical: spaces.spacingMd,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.row,
                        {
                            gap: spaces.spacingSm
                        }
                    ]}
                >
                    <Sidebar.Trigger/>
                    <Breadcrumb
                        accessibilityLabel={t("blocks.settings01.breadcrumb")}
                    >
                        <Breadcrumb.List>
                            <Breadcrumb.Item>
                                <Breadcrumb.Link
                                    title={t("blocks.settings01.navSettings")}
                                    onPress={handlePress}
                                />
                            </Breadcrumb.Item>
                            <Breadcrumb.Item>
                                <Breadcrumb.Separator/>
                                <Breadcrumb.Page
                                    title={t("blocks.settings01.sections.security")}
                                />
                            </Breadcrumb.Item>
                        </Breadcrumb.List>
                    </Breadcrumb>
                </View>
                <Badge.Indicator
                    spreadBehaviour="free"
                    type="danger"
                >
                    <Button
                        accessibilityLabel={t("blocks.settings01.notifications")}
                        spreadBehaviour="free"
                        onPress={handlePress}
                        variant="ghost"
                        icon={BellIcon}
                        type="neutral"
                        size="small"
                    />
                </Badge.Indicator>
            </View>
            <Separator/>
            <View
                style={[
                    stylesheet.page,
                    {
                        padding: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.column,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <Text
                        variant="headlineMediumSize"
                        color="high"
                    >
                        {t("blocks.settings01.title")}
                    </Text>
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("blocks.settings01.description")}
                    </Text>
                </View>
                <Tabs
                    orientation={isCompact ? "horizontal" : "vertical"}
                    overflowBehaviour="scroll"
                    onValueChange={setSection}
                    style={stylesheet.tabs}
                    value={section}
                    variant="line"
                >
                    <Tabs.List
                        style={isCompact ? undefined : stylesheet.list}
                        title={t("blocks.settings01.tabsTitle")}
                    >
                        {
                            SECTIONS.map((key) => <Tabs.Trigger
                                title={t(`blocks.settings01.sections.${key}`)}
                                value={key}
                                key={key}
                            />)
                        }
                    </Tabs.List>
                    {
                        SECTIONS.map((key) => <Tabs.Content
                            style={stylesheet.content}
                            value={key}
                            key={key}
                        >
                            {key === "security" ? renderSecurity() : renderOther()}
                        </Tabs.Content>)
                    }
                </Tabs>
            </View>
        </Sidebar.Inset>
    </Sidebar.Provider>;
};
export default Settings01;
